import React, { useState, useEffect } from 'react';
import { Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { enqueueSnackbar } from 'notistack';
import { ExportHubAPI, ExportProduct } from 'api/ExportHubAPI';
import StyledSingleSelect from './StyledSingleSelect';

interface Props {
  open: boolean;
  mode: 'child' | 'parent';
  product: ExportProduct;
  databaseId: string;
  groupId: string | undefined;
  excludeIds: string[];
  onClose: () => void;
  onLinked: () => void;
}

const CHILD_TIERS = new Set(['Component', 'Sub-assembly']);
const PARENT_TIERS = new Set(['Sub-assembly', 'Assembly']);

const BomLinkModal: React.FC<Props> = ({
  open,
  mode,
  product,
  databaseId,
  groupId,
  excludeIds,
  onClose,
  onLinked,
}) => {
  const [products, setProducts] = useState<ExportProduct[]>([]);
  const [selected, setSelected] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !groupId) return;
    setSelected('');
    setQuantity('1');
    ExportHubAPI.getProducts(databaseId, groupId, { limit: 200 })
      .then((res) => setProducts(res.products))
      .catch(() => setProducts([]));
  }, [open, databaseId, groupId]);

  const allowed = mode === 'child' ? CHILD_TIERS : PARENT_TIERS;
  const exclude = new Set([product.id, ...excludeIds]);
  const candidates = products.filter(
    (p) => allowed.has(p.tier) && !exclude.has(p.id),
  );

  const confirm = async () => {
    if (!groupId || !selected) return;
    setSaving(true);
    try {
      const parentId = mode === 'child' ? product.id : selected;
      const childId = mode === 'child' ? selected : product.id;
      await ExportHubAPI.addBomLink({
        group_id: groupId,
        parent_product_id: parentId,
        child_product_id: childId,
        quantity: quantity.trim()
          ? Math.max(0, Math.floor(Number(quantity)))
          : undefined,
      });
      enqueueSnackbar(mode === 'child' ? 'Child added.' : 'Parent added.', {
        variant: 'success',
      });
      onLinked();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to add link.';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} className='eh-modal-root'>
      <div className='eh-modal' role='dialog' aria-label='Add BOM link'>
        <div className='eh-modal-head'>
          <h2 className='eh-modal-title'>
            {mode === 'child' ? 'Add Child Product' : 'Add Parent Assembly'}
          </h2>
          <button
            className='eh-modal-close'
            onClick={onClose}
            aria-label='Close'
            type='button'
          >
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        <div className='eh-modal-body'>
          {candidates.length === 0 ? (
            <div className='eh-detail-note'>
              No eligible{' '}
              {mode === 'child'
                ? 'components or sub-assemblies'
                : 'sub-assemblies or assemblies'}{' '}
              available to link.
            </div>
          ) : (
            <>
              <div className='eh-field'>
                <span className='eh-field-label'>
                  {mode === 'child' ? 'Child product' : 'Parent assembly'}
                </span>
                <StyledSingleSelect
                  options={[
                    { value: '', label: 'Select…' },
                    ...candidates.map((p) => ({
                      value: p.id,
                      label: `${p.name} — ${p.tier}`,
                    })),
                  ]}
                  value={selected}
                  onChange={setSelected}
                  inline
                />
              </div>
              <label className='eh-field'>
                <span className='eh-field-label'>Quantity</span>
                <input
                  className='eh-input'
                  type='number'
                  min={0}
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
              </label>
            </>
          )}
        </div>

        <div className='eh-modal-foot'>
          <button
            className='btn-outline'
            type='button'
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className='btn-primary'
            type='button'
            onClick={confirm}
            disabled={saving || !selected}
            style={{ opacity: saving || !selected ? 0.6 : 1 }}
          >
            {saving ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default BomLinkModal;
